import { type RefObject, useCallback, useEffect } from 'react';
import { useAIEdit } from './ai-edit-context';

/**
 * Registers keyboard shortcuts to toggle the AI edit panel:
 * - Cmd+I: Toggle from anywhere
 * - Cmd+K: Toggle only when focus is inside `containerRef`
 *   (container-scoped to avoid conflicting with global search)
 *
 * The consumer provides the `containerRef` — typically the ref from
 * QueryEditor.Container — and controls enablement (e.g., behind a
 * feature flag).
 */
export function useAIEditShortcut({
  enabled,
  containerRef,
}: {
  enabled: boolean;
  containerRef: RefObject<HTMLElement | null>;
}) {
  const { isOpen, open, close } = useAIEdit();

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        (e.metaKey && e.key === 'i') ||
        (e.metaKey && !e.shiftKey && e.key === 'k' && containerRef.current?.contains(document.activeElement))
      ) {
        e.preventDefault();
        e.stopPropagation();
        if (isOpen) {
          close();
        } else {
          open();
        }
      }
    },
    [isOpen, open, close, containerRef]
  );

  useEffect(() => {
    if (!enabled) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
}
